import React, {
    useState,
    useRef
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import parse from 'html-react-parser';

import {Dialog} from 'primereact/dialog';
import {Button} from 'primereact/button';
import {InputText} from 'primereact/inputtext';
import {Password} from 'primereact/password';
import {ProgressSpinner} from 'primereact/progressspinner';

import login from '~/frontend/src/actions/login';
import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import XhrErrorDialog from '~/frontend/src/components/base/dialogs/XhrErrorDialog';
import {isInvalidHTML} from '~/frontend/src/utilities/valueRenderers';


type Props = {
    userNameEmptyText? : string;
    passwordEmptyText? : string;
}

/**
 * Login dialog
 * @return {JSX.Element}
 * @constructor
 */
const LoginDialog = ( props : Props ) => {
    const dispatch : AppDispatch    = useDispatch(),
          logingIn                  = useSelector( ( state : StoreState ) => state.login?.loggingIn ),
          loginError                = useSelector( ( state : StoreState ) => state.xhr?.errors?.[ 'global' ] ),
          lastUser                  = localStorage.getItem( 'lastUser' ),
          loginDialogVisible        = !logingIn && !loginError,
          loggingInDialogVisible    = logingIn && !loginError,
          usernameInput             = useRef<HTMLInputElement>( null ),
          [ userName, setUserName ] = useState( lastUser ?? '' ),
          passwordInput             = useRef<Password & HTMLInputElement>( null ),
          [ password, setPassword ] = useState( '' ),
          header                    = <div className="yn-login-dialog-header yoonite-logo yn-not-responsive"/>,
          footer                    = <div>
              <Button label="Login" className="p-button-outlined" disabled={!userName || !password} onClick={() => {
                  if ( userName && password ) {
                      global.loginAfterSessionExpired = false;
                      dispatch( login( userName, password ) );
                  }
              }}/>
          </div>;

    let message : React.ReactNode | null = null;

    if ( global.loginAfterSessionExpired ) {
        let msg = __( 'YN.base.common.login.sessionExpired' );

        if ( !isInvalidHTML( msg ) ) {
            message = <div className="yn-blue mb-4">{parse( msg )}</div>;
        }
        else {
            message = <div className="yn-blue mb-4">{msg}</div>;
        }
    }

    return <>
        <Dialog
            id="loginDialog"
            modal={false}
            content=""
            header={header}
            footer={footer}
            visible={loginDialogVisible}
            closable={false}
            closeOnEscape={false}
            className="yn-login-dialog"
            onHide={() => {
            }}>
            {message}
            <form>
                <InputText
                    ref={usernameInput}
                    className="mb-2 block"
                    id="loginDialog_username"
                    name="username"
                    placeholder={props.userNameEmptyText}
                    autoComplete="username"
                    autoFocus={!userName}
                    style={{ width: '100%' }}
                    value={userName}
                    pt={{
                        root: {
                            onChange : ( event : React.ChangeEvent<HTMLInputElement> ) => {
                                setUserName( event.target.value );
                            },
                            onKeyDown: ( event ) => {
                                if ( event.key === 'Enter' ) {
                                    if ( userName && password ) {
                                        dispatch( login( userName, password ) );
                                    }
                                    else {
                                        ( passwordInput?.current?.getInput() as unknown as HTMLInputElement ).focus();
                                    }
                                }
                            }
                        }
                    }}
                />
                <Password
                    ref={passwordInput}
                    className="block"
                    id="loginDialog_password"
                    name="password"
                    placeholder={props.passwordEmptyText}
                    autoComplete="current-password"
                    autoFocus={!!userName}
                    style={{ width: '100%' }}
                    inputStyle={{ width: '100%' }}
                    value={password}
                    feedback={false}
                    onChange={( e ) => setPassword( e.target.value )}
                    onKeyDown={event => {
                        if ( event.key === 'Enter' ) {
                            if ( userName && password ) {
                                dispatch( login( userName, password ) );
                            }
                            else {
                                usernameInput?.current?.focus();
                            }
                        }
                    }}
                />
            </form>
        </Dialog>
        <Dialog
            id="logingInDialog"
            modal
            content=""
            showHeader={false}
            visible={loggingInDialogVisible}
            closable={false}
            className="yn-login-dialog"
            contentStyle={{ textAlign: 'center', overflow: 'hidden' }}
            onHide={() => {
            }}>
            <ProgressSpinner/><br/>
            Logging in...
        </Dialog>
        <XhrErrorDialog
            dataContext="login"
        />
    </>;
}

LoginDialog.defaultProps = {
    userNameEmptyText: 'Username',
    passwordEmptyText: 'Password'
};

export default LoginDialog;
